import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { FloorPlan, Unit } from 'src/app/core/shared/interface';
import { PropertyService } from '../../properties/service/property.service';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { AvailabilityStatus, AvailabilityStatusText, VisibilityStatus, VisibilityStatusText } from '../../../enums/units.enum';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { CarouselModule } from 'primeng/carousel';
import { Galleria, GalleriaModule } from 'primeng/galleria';

@Component({
  selector: 'app-units-form',
  standalone: true,
  imports: [ButtonModule, FormsModule, CommonModule, DropdownModule, CalendarModule, CheckboxModule, DialogModule, CarouselModule, GalleriaModule],
  templateUrl: './units-form.component.html',
  styleUrl: './units-form.component.scss'
})
export class UnitsFormComponent implements OnChanges {
  private readonly maxMediaCount = 5;
  private readonly maxMediaSizeBytes = 5 * 1024 * 1024;
  @ViewChild('galleria') galleria!: Galleria;
  
  // ✅ Fixed
  @Output() mediaOpened = new EventEmitter<{ mediaList: any[], index: number }>();

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!this.showMediaModal) return;
    if (event.key === 'ArrowRight') this.nextMedia();
    if (event.key === 'ArrowLeft') this.prevMedia();
    if (event.key === 'Escape') this.showMediaModal = false;
  }

  maxVisibilityDate: Date | null = null;
  floorPlan: FloorPlan[] = [];
  tenants: any = [];
  minDate: Date = new Date();

  @Input() unit: Unit = {} as Unit;
  @Output() save = new EventEmitter<FormData>();

  imagePreviewUrl = '';
  selectedImage: File | null = null;
  existingImages: any[] = [];
  existingVideos: any[] = [];
  selectedImages: File[] = [];
  selectedVideos: File[] = [];
  selectedImagePreviews: string[] = [];
  selectedVideoPreviews: string[] = [];
  deletingExistingImageIds: string[] = [];
  deletingExistingVideoIds: string[] = [];
  mediaError = '';
  lightboxVisible = false;
  lightboxItems: Array<{ type: 'image' | 'video'; src: string; title: string }> = [];
  activeLightboxIndex = 0;
  imageError = '';
  videoError = '';
  imageHasBlockingError = false;
  videoHasBlockingError = false;
  today = new Date();
  availabilityDisabled = false;
  visibilityDisabled = false;
  AvailabilityStatus = AvailabilityStatus;
  AvailabilityStatusText = AvailabilityStatusText;
  VisibilityStatus = VisibilityStatus;
  VisibilityStatusText = VisibilityStatusText;
  mediaCarouselResponsiveOptions = [
    {
      breakpoint: '1400px',
      numVisible: 3,
      numScroll: 1,
    },
    {
      breakpoint: '1024px',
      numVisible: 2,
      numScroll: 1
    },
    {
      breakpoint: '992px',
      numVisible: 2,
      numScroll: 1,
    },
    {
      breakpoint: '768px',
      numVisible: 2,
      numScroll: 1
    },
    {
      breakpoint: '576px',
      numVisible: 1,
      numScroll: 1,
    },
    {
      breakpoint: '560px',
      numVisible: 1,
      numScroll: 1
    }
  ];
  showMediaModal = false;
  activeModalIndex = 0;
  isRemovingMedia = false;

  galleriaActiveIndex = 0;
  galleriaImages: { item: any, type: 'image' | 'video' }[] = [];
  lastPointerDownTime = 0;
  dragging = false;
  startX = 0;
  suppressNextOpen = false;
  constructor(private propertyService: PropertyService) { }

  ngOnChanges(changes: SimpleChanges): void {
    const currentUnit = changes['unit']?.currentValue as Unit | undefined;
    if (!currentUnit) {
      return;
    }

    this.unit.availability_status = this.unit.availability_status || AvailabilityStatus.DATE;
    this.unit.visibility_status = this.unit.visibility_status || VisibilityStatus.DATE;
    this.unit.availablity_date = this.unit.availablity_date ? new Date(this.unit.availablity_date) : new Date();
    this.unit.visibility_date = this.unit.visibility_date ? new Date(this.unit.visibility_date) : new Date();

    this.applyAvailabilityStatusLogic();
    this.applyVisibilityStatusLogic();

    this.resetSelectedMedia();
    this.existingImages = this.extractMedia(currentUnit, 'image');
    this.existingVideos = this.extractMedia(currentUnit, 'video');
    this.selectedImage = null;
    this.imagePreviewUrl = this.existingImages[0]?.uri || '';
    this.imageError = '';
    this.videoError = '';
    this.mediaError = '';
    this.imageHasBlockingError = false;
    this.videoHasBlockingError = false;
    this.unit['image_title'] = this.existingImages[0]?.title || '';
    this.getAllFloorPlans();
    this.getAllTenants();
    this.updateVisibilityMinDate();

    this.populateGalleriaImages();
  }

  getAllTenants(): void {
    this.propertyService.getTenants({ limit: 1000 }).subscribe({
      next: (response) => {
        this.tenants = response.data.map((tenant: any) => ({
          ...tenant,
          label: tenant.first_name + ' ' + tenant.last_name,
          value: tenant._id,
        }));
      }
    });
  }

  onMediaSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const files = Array.from(input.files);
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));
    const videoFiles = files.filter((file) => file.type.startsWith('video/'));
    const currentTotalMediaCount = this.getCurrentTotalMediaCount();
    this.mediaError = '';
    let runningTotalCount = currentTotalMediaCount;

    if (imageFiles.length) {
      const imageResult = this.processFiles(imageFiles, 'image', runningTotalCount);
      this.imageError = imageResult.error;
      this.imageHasBlockingError = imageResult.blockSubmission;

      if (imageResult.files.length) {
        this.selectedImages.push(...imageResult.files);
        const newPreviewUrls = imageResult.files.map((file) => URL.createObjectURL(file));
        this.selectedImagePreviews.push(...newPreviewUrls);
        this.selectedImage = this.selectedImages[0] || null;
        this.imagePreviewUrl = this.selectedImagePreviews[0] || this.existingImages[0]?.uri || '';
        this.unit['image_title'] = this.selectedImages[0]?.name || this.existingImages[0]?.title || '';
        runningTotalCount += imageResult.files.length;
      }
    } else {
      this.imageError = '';
      this.imageHasBlockingError = false;
    }

    if (videoFiles.length) {
      const videoResult = this.processFiles(videoFiles, 'video', runningTotalCount);
      this.videoError = videoResult.error;
      this.videoHasBlockingError = videoResult.blockSubmission;

      if (videoResult.files.length) {
        this.selectedVideos.push(...videoResult.files);
        const newPreviewUrls = videoResult.files.map((file) => URL.createObjectURL(file));
        this.selectedVideoPreviews.push(...newPreviewUrls);
        runningTotalCount += videoResult.files.length;
      }
    } else {
      this.videoError = '';
      this.videoHasBlockingError = false;
    }

    const unsupportedFiles = files.filter(
      (file) => !file.type.startsWith('image/') && !file.type.startsWith('video/'),
    );
    if (unsupportedFiles.length) {
      const message = 'Only image and video files are allowed.';
      this.mediaError = message;
      this.imageHasBlockingError = true;
      this.videoHasBlockingError = true;
    }

    if (this.imageError && this.imageError === this.videoError) {
      this.mediaError = this.imageError;
      this.imageError = '';
      this.videoError = '';
    }

    input.value = '';
  }

  onSubmit(form: any): void {
    if (form.valid && !this.hasMediaValidationErrors()) {
      this.syncOptionalMediaState();

      if (
        this.unit.availability_status !== AvailabilityStatus.DATE &&
        this.unit.availability_status !== AvailabilityStatus.AVAILABLENOW
      ) {
        this.unit.availablity_date = null;
      }

      if (
        this.unit.visibility_status !== VisibilityStatus.DATE &&
        this.unit.visibility_status !== VisibilityStatus.VISIBLENOW
      ) {
        this.unit.visibility_date = null;
      }

      const now = new Date();
      this.unit.is_visible = false;
      if (this.unit.visibility_status === VisibilityStatus.VISIBLENOW) {
        this.unit.is_visible = true;
      } else if (
        this.unit.visibility_status === VisibilityStatus.DATE &&
        this.unit.visibility_date &&
        new Date(this.unit.visibility_date) <= now
      ) {
        this.unit.is_visible = true;
      }

      const payload = new FormData();
      payload.append('unit', JSON.stringify(this.unit));
      payload.append('existingImages', JSON.stringify(this.existingImages));
      payload.append('existingVideos', JSON.stringify(this.existingVideos));

      this.selectedImages.forEach((image) => payload.append('images', image));
      this.selectedVideos.forEach((video) => payload.append('videos', video));

      this.save.emit(payload);
    }
  }

  removeImage() {
    this.resetSelectedImages();
    this.existingImages = [];
    this.selectedImage = null;
    this.unit['image_title'] = '';
    this.unit['image'] = null;
    this.unit['images'] = null as any;
    this.imagePreviewUrl = '';
  }

  removeExistingImage(index: number) {
    const image = this.existingImages[index];
    if (!image) {
      return;
    }

    if (!image._id) {
      this.removeExistingImageLocally(index);
      return;
    }

    this.imageError = '';
    this.deletingExistingImageIds.push(image._id);
    this.propertyService.deleteImage(image._id).subscribe({
      next: () => {
        this.removeExistingImageLocally(index);
        this.deletingExistingImageIds = this.deletingExistingImageIds.filter((id) => id !== image._id);
      },
      error: () => {
        this.deletingExistingImageIds = this.deletingExistingImageIds.filter((id) => id !== image._id);
        this.imageError = 'Unable to remove image. Please try again.';
      }
    });
  }

  removeSelectedImage(index: number) {
    const previewUrl = this.selectedImagePreviews[index];
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    this.selectedImages.splice(index, 1);
    this.selectedImagePreviews.splice(index, 1);
    this.selectedImage = this.selectedImages[0] || null;
    this.imagePreviewUrl = this.selectedImagePreviews[0] || this.existingImages[0]?.uri || '';
    this.unit['image_title'] = this.selectedImages[0]?.name || this.existingImages[0]?.title || '';
    if (!this.existingImages.length && !this.selectedImages.length) {
      this.unit['image'] = null;
      this.unit['images'] = null as any;
    }
  }

  removeExistingVideo(index: number) {
    const video = this.existingVideos[index];
    if (!video) {
      return;
    }

    if (!video._id) {
      this.removeExistingVideoLocally(index);
      return;
    }

    this.videoError = '';
    this.deletingExistingVideoIds.push(video._id);
    this.propertyService.deleteImage(video._id).subscribe({
      next: () => {
        this.removeExistingVideoLocally(index);
        this.deletingExistingVideoIds = this.deletingExistingVideoIds.filter((id) => id !== video._id);
      },
      error: () => {
        this.deletingExistingVideoIds = this.deletingExistingVideoIds.filter((id) => id !== video._id);
        this.videoError = 'Unable to remove video. Please try again.';
      }
    });
  }

  removeSelectedVideo(index: number) {
    const previewUrl = this.selectedVideoPreviews[index];
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    this.selectedVideos.splice(index, 1);
    this.selectedVideoPreviews.splice(index, 1);
    if (!this.existingVideos.length && !this.selectedVideos.length) {
      this.unit['videos'] = null as any;
    }
  }

  propertyId = '64c123d4d259ca5b364c45a3';
  getAllFloorPlans(): void {
    this.propertyService.getFloorPlans().subscribe({
      next: (response) => {
        this.floorPlan = response;
      }
    });
  }

  updateVisibilityMinDate() {
    if (this.unit.availablity_date) {
      this.maxVisibilityDate = new Date(this.unit.availablity_date);
    } else {
      this.maxVisibilityDate = null;
    }
  }

  onFloorPlanChange(event: any): void {
    this.unit.floor_plan_id = event.value;
    const selectedPlan = this.floorPlan.find(plan => plan._id === event.value);
    if (selectedPlan) {
      this.unit.beds = selectedPlan.beds;
      this.unit.baths = selectedPlan.baths;
      this.unit.min_price = selectedPlan.min_price;
      this.unit.max_price = selectedPlan.max_price;
      this.unit.sq_ft = selectedPlan.sq_ft;
    }
  }

  validateNumber(event: Event) {
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart || 0;

    let value = input.value
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*?)\./g, '$1');

    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    if (parts.length === 2 && parts[1].length > 2) {
      parts[1] = parts[1].slice(0, 2);
      value = parts.join('.');
    }

    if (value !== input.value) {
      input.value = value;
      const newPos = Math.min(start, value.length);
      input.setSelectionRange(newPos, newPos);
    }
  }

  validateMinMax() {
    const min = this.unit.min_price;
    const max = this.unit.max_price;

    if (min != null && max != null && max < min) {
      this.unit.max_price = min;
    }
  }

  isUnitPriceValid(): boolean {
    return (
      this.unit.min_price != null && Number(this.unit.min_price) > 0 &&
      this.unit.max_price != null && Number(this.unit.max_price) > 0
    );
  }

  onAvailabilityChange() {
    this.applyAvailabilityStatusLogic();
  }

  applyAvailabilityStatusLogic() {
    const s = this.unit.availability_status;

    if (s === AvailabilityStatus.DATE) {
      this.availabilityDisabled = false;
    }
    else if (s === AvailabilityStatus.AVAILABLENOW) {
      this.unit.availablity_date = this.today;
      this.availabilityDisabled = true;
    }
    else if (s === AvailabilityStatus.NOTAVAILABLE) {
      this.unit.availablity_date = null;
      this.availabilityDisabled = true;
    }
  }

  applyVisibilityStatusLogic() {
    const s = this.unit.visibility_status;

    if (s === VisibilityStatus.DATE) {
      this.visibilityDisabled = false;
    }
    else if (s === VisibilityStatus.VISIBLENOW) {
      this.unit.visibility_date = this.today;
      this.visibilityDisabled = true;
    }
    else if (s === VisibilityStatus.NOTVISIBLE) {
      this.unit.visibility_date = null;
      this.visibilityDisabled = true;
    }
  }

  onVisibilityChange() {
    this.applyVisibilityStatusLogic();
  }

  hasMediaValidationErrors(): boolean {
    return this.imageHasBlockingError || this.videoHasBlockingError;
  }

  get existingMediaItems() {
    return [
      ...this.existingImages.map((image, index) => ({
        mediaType: 'image' as const,
        index,
        src: image.uri,
        title: image.title || image.file_name || `Image ${index + 1}`,
        item: image,
      })),
      ...this.existingVideos.map((video, index) => ({
        mediaType: 'video' as const,
        index,
        src: video.uri,
        title: video.title || video.file_name || `Video ${index + 1}`,
        item: video,
      })),
    ];
  }

  get selectedMediaItems() {
    return [
      ...this.selectedImagePreviews.map((preview, index) => ({
        source: 'selected' as const,
        mediaType: 'image' as const,
        index,
        src: preview,
        url: preview,
        title: this.selectedImages[index]?.name || `Image ${index + 1}`,
        name: this.selectedImages[index]?.name || `Image ${index + 1}`,
      })),
      ...this.selectedVideoPreviews.map((preview, index) => ({
        source: 'selected' as const,
        mediaType: 'video' as const,
        index,
        src: preview,
        url: preview,
        title: this.selectedVideos[index]?.name || `Video ${index + 1}`,
        name: this.selectedVideos[index]?.name || `Video ${index + 1}`,
      })),
    ];
  }

  get mediaFiles() {
    return [
      ...this.existingImages.map((image, index) => ({
        source: 'existing' as const,
        mediaType: 'image' as const,
        type: 'image' as const,
        index,
        src: image.uri,
        url: image.uri,
        title: image.title || image.file_name || `Image ${index + 1}`,
        name: image.title || image.file_name || `Image ${index + 1}`,
        item: image,
      })),
      ...this.existingVideos.map((video, index) => ({
        source: 'existing' as const,
        mediaType: 'video' as const,
        type: 'video' as const,
        index,
        src: video.uri,
        url: video.uri,
        title: video.title || video.file_name || `Video ${index + 1}`,
        name: video.title || video.file_name || `Video ${index + 1}`,
        item: video,
      })),
      ...this.selectedMediaItems.map((media) => ({
        ...media,
        type: media.mediaType,
      })),
    ];
  }

  closeLightbox() {
    this.lightboxVisible = false;
    this.lightboxItems = [];
    this.activeLightboxIndex = 0;
  }

  showPreviousLightboxItem() {
    if (this.lightboxItems.length < 2) {
      return;
    }
    this.activeLightboxIndex =
      (this.activeLightboxIndex - 1 + this.lightboxItems.length) % this.lightboxItems.length;
  }

  showNextLightboxItem() {
    if (this.lightboxItems.length < 2) {
      return;
    }
    this.activeLightboxIndex = (this.activeLightboxIndex + 1) % this.lightboxItems.length;
  }

  get activeLightboxItem() {
    return this.lightboxItems[this.activeLightboxIndex] || null;
  }

  openExistingMediaLightbox(index: number) {
    this.openLightbox(
      this.existingMediaItems.map((media) => ({
        type: media.mediaType,
        src: media.src,
        title: media.title,
      })),
      index,
    );
  }

  openSelectedMediaLightbox(index: number) {
    this.openLightbox(
      this.selectedMediaItems.map((media) => ({
        type: media.mediaType,
        src: media.src,
        title: media.title,
      })),
      index,
    );
  }

  removeExistingMedia(media: { mediaType: 'image' | 'video'; index: number }, event?: Event) {
    if (event) event.stopPropagation();
    if (media.mediaType === 'image') {
      this.removeExistingImage(media.index);
      return;
    }
    this.removeExistingVideo(media.index);
  }

  removeSelectedMedia(media: { mediaType: 'image' | 'video'; index: number }) {
    if (media.mediaType === 'image') {
      this.removeSelectedImage(media.index);
      return;
    }
    this.removeSelectedVideo(media.index);
  }

  isDeletingExistingMedia(media: { mediaType: 'image' | 'video'; item: any }): boolean {
    return media.mediaType === 'image'
      ? this.isDeletingExistingImage(media.item)
      : this.isDeletingExistingVideo(media.item);
  }

  triggerMediaInput(): void {
    const input = document.getElementById('media') as HTMLInputElement | null;
    input?.click();
  }

  openModal(event: MouseEvent, media: any) {
    if (this.isRemovingMedia) {
      this.isRemovingMedia = false;
      return;
    }
    if (this.showMediaModal) return;  // ADD THIS
    const index = this.mediaFiles.findIndex(
      (m) => m.source === media.source &&
        m.mediaType === media.mediaType &&
        m.index === media.index
    );
    this.activeModalIndex = index !== -1 ? index : 0;
    this.showMediaModal = true;
  }

  onThumbClick(event: MouseEvent, media: any): void {
    if (this.isRemovingMedia) {
      this.isRemovingMedia = false;
      return;
    }
    if (Math.abs(event.movementX) < 5 && Math.abs(event.movementY) < 5) {
      event.stopPropagation();
      if (this.showMediaModal) return;
      const index = this.mediaFiles.findIndex(
        (m) => m.source === media.source &&
          m.mediaType === media.mediaType &&
          m.index === media.index
      );
      this.activeModalIndex = index !== -1 ? index : 0;
      this.showMediaModal = true;
    }
  }

  onRemoveMedia(event: MouseEvent, media: any): void {
    this.isRemovingMedia = true;
    event.stopPropagation();
    this.removeExistingMedia(media);
  }

  onRemoveMediaPointerDown(event: Event, media: { source: 'existing' | 'selected'; mediaType: 'image' | 'video'; index: number }): void {
    this.suppressNextOpen = true;
    this.onRemoveMediaClick(event, media);
  }

  onRemoveMediaClick(event: any, media: { source: 'existing' | 'selected'; mediaType: 'image' | 'video'; index: number }): void {
    const nativeEvent = event?.originalEvent ?? event;
    this.isRemovingMedia = true;
    nativeEvent?.preventDefault?.();
    nativeEvent?.stopPropagation?.();
    this.removeMedia(media);
  }

  removeMedia(media: { source: 'existing' | 'selected'; mediaType: 'image' | 'video'; index: number }): void {
    if (media.source === 'existing') {
      this.removeExistingMedia(media);
    } else {
      this.removeSelectedMedia(media);
    }
  }

  getMediaIndex(media: any): number {
    return this.mediaFiles.findIndex((item) =>
      item.source === media.source &&
      item.mediaType === media.mediaType &&
      item.index === media.index
    );
  }

  private processFiles(files: File[], mediaType: 'image' | 'video', startingTotalCount = this.getCurrentTotalMediaCount()) {
    const acceptedFiles: File[] = [];
    let error = '';
    let blockSubmission = false;

    for (const file of files) {
      if (!file.type.startsWith(`${mediaType}/`)) {
        error = `Only ${mediaType} files are allowed.`;
        blockSubmission = true;
        continue;
      }

      if (file.size > this.maxMediaSizeBytes) {
        error = `Each ${mediaType} must be 5 MB or smaller.`;
        blockSubmission = true;
        continue;
      }

      if (startingTotalCount + acceptedFiles.length >= this.maxMediaCount) {
        error = `Only ${this.maxMediaCount} total media files are allowed.`;
        break;
      }

      acceptedFiles.push(file);
    }

    return { files: acceptedFiles, error, blockSubmission };
  }

  private resetSelectedMedia() {
    this.resetSelectedImages();
    this.selectedVideoPreviews.forEach((url) => URL.revokeObjectURL(url));
    this.selectedVideos = [];
    this.selectedVideoPreviews = [];
  }

  private resetSelectedImages() {
    this.selectedImagePreviews.forEach((url) => URL.revokeObjectURL(url));
    this.selectedImages = [];
    this.selectedImagePreviews = [];
  }

  private syncOptionalMediaState() {
    const hasImages = this.existingImages.length > 0 || this.selectedImages.length > 0;
    const hasVideos = this.existingVideos.length > 0 || this.selectedVideos.length > 0;

    this.unit['image'] = hasImages ? this.existingImages[0]?.uri || null : null;
    this.unit['images'] = hasImages ? this.existingImages : null as any;
    this.unit['image_title'] = hasImages
      ? this.selectedImages[0]?.name || this.existingImages[0]?.title || ''
      : '';
    this.unit['videos'] = hasVideos ? this.existingVideos : null as any;
  }

  private normalizeMediaItem(item: any) {
    return {
      ...item,
      uri: item?.uri || item?.url || item?.image || item?.video || '',
      title: item?.title || item?.file_name || item?.name || '',
    };
  }

  private extractMedia(unit: any, mediaType: 'image' | 'video') {
    const directItems = mediaType === 'image' ? unit?.images : unit?.videos;
    if (Array.isArray(directItems) && directItems.length) {
      return directItems.map((item: any) => this.normalizeMediaItem(item));
    }

    const mediaItems = Array.isArray(unit?.media) ? unit.media : [];
    const filteredMedia = mediaItems
      .filter((item: any) => item?.file_type?.startsWith(`${mediaType}/`))
      .map((item: any) => this.normalizeMediaItem(item));
    if (filteredMedia.length) {
      return filteredMedia;
    }

    if (mediaType === 'image' && unit?.image) {
      return [this.normalizeMediaItem({ uri: unit.image, title: unit.image_title })];
    }

    return [];
  }

  isDeletingExistingImage(image: any): boolean {
    return !!image?._id && this.deletingExistingImageIds.includes(image._id);
  }

  isDeletingExistingVideo(video: any): boolean {
    return !!video?._id && this.deletingExistingVideoIds.includes(video._id);
  }

  private removeExistingImageLocally(index: number) {
    this.existingImages.splice(index, 1);
    this.imagePreviewUrl = this.selectedImagePreviews[0] || this.existingImages[0]?.uri || '';
    this.unit['image_title'] = this.selectedImages[0]?.name || this.existingImages[0]?.title || '';
    if (!this.existingImages.length && !this.selectedImages.length) {
      this.unit['image'] = null;
      this.unit['images'] = null as any;
    }
  }

  private removeExistingVideoLocally(index: number) {
    this.existingVideos.splice(index, 1);
    if (!this.existingVideos.length && !this.selectedVideos.length) {
      this.unit['videos'] = null as any;
    }
  }

  private getCurrentTotalMediaCount() {
    return (
      this.existingImages.length +
      this.existingVideos.length +
      this.selectedImages.length +
      this.selectedVideos.length
    );
  }

  private openLightbox(items: Array<{ type: 'image' | 'video'; src: string; title: string }>, index: number) {
    if (!items.length) {
      return;
    }
    this.lightboxItems = items;
    this.activeLightboxIndex = index;
    this.lightboxVisible = true;
  }

  prevMedia() {
    this.activeModalIndex =
      this.activeModalIndex > 0
        ? this.activeModalIndex - 1
        : this.mediaFiles.length - 1;
  }

  nextMedia() {
    this.activeModalIndex =
      this.activeModalIndex < this.mediaFiles.length - 1
        ? this.activeModalIndex + 1
        : 0;
  }

  populateGalleriaImages() {
    this.galleriaImages = this.mediaFiles.map(media => ({
      item: media.src,
      type: media.type
    }));
  }

  openGalleria(index: number) {
    this.galleriaActiveIndex = index;
    //this.galleria.show();
    this.lightboxVisible = true;
    //this.showMediaModal = true;
  }

  removeMediaG(index: number) {
    this.mediaFiles.splice(index, 1);
    this.populateGalleriaImages();
  }

  handleClick(event: PointerEvent, index: number) {
    const now = new Date().getTime();

    // Prevent drag from opening the modal
    if (now - this.lastPointerDownTime < 200) {
      // considered as a click
      this.openGalleria(index);
    }

    this.lastPointerDownTime = now;
  }

  onPointerDown(event: PointerEvent) {
    this.dragging = false;
    this.startX = event.clientX;
  }

  onPointerMove(event: PointerEvent) {
    if (Math.abs(event.clientX - this.startX) > 5) {
      this.dragging = true; // user is dragging
    }
  }

  onPointerUp(event: PointerEvent, index: number, media: any) {
    if (this.suppressNextOpen) {
      this.suppressNextOpen = false;
      return;
    }

    if (this.isRemoveActionTarget(event)) {
      return;
    }

    if (this.isRemovingMedia) {
      this.isRemovingMedia = false;
      return;
    }
    if (!this.dragging) { // Emit media data to parent
      const clickedIndex = this.getMediaIndex(media);
      const mediaList = this.mediaFiles.map((item) => ({
        uri: item.url || item.src,
        url: item.url || item.src,
        src: item.src || item.url,
        type: item.type,
        title: item.title,
      }));
      this.mediaOpened.emit({ mediaList, index: clickedIndex !== -1 ? clickedIndex : index });
    }
  }

  private isRemoveActionTarget(event: Event): boolean {
    const target = event.target as HTMLElement | null;
    return !!target?.closest('.thumb-remove-btn, .p-button, .p-button-icon, .p-button-icon-only');
  }
}